/**
 * Editor de plantillas en 3 pasos: el mismo en la página de siempre (`plantillas/nueva` y
 * `plantillas/:id/editar`) y en la plantilla de una campaña (`campanas/:id/plantilla`, SPEC-BD C1:
 * crear una plantilla sin perder la campaña).
 *
 * La verdad sobre si una plantilla pasa las reglas de Meta la tiene el backend (`POST
 * /plantillas/validar`): el editor la pide en vivo y el botón «Enviar a aprobación de Meta» sólo se
 * habilita cuando la última validación corresponde EXACTAMENTE a lo que hay en pantalla y no tiene
 * errores (`puedeEnviarAAprobacion`, fail-closed). Una plantilla rechazada cuesta entre minutos y
 * 48 h de espera y, si es una promo mal categorizada, plata: el botón apagado es más barato.
 *
 * Lo que el operador no tendría por qué acordarse (el idioma de la marca) lo pone
 * `aplicarReglasDeCategoria` en cada cambio. En una promoción, el pie y el botón de baja se precargan con
 * los de Configuración y se pueden cambiar o sacar en esa plantilla (pedido de Alan del 16/10/2026): si
 * falta la baja, el validador avisa y no frena.
 *
 * El editor no navega: avisa con callbacks (`onCreada`, `onEnviada`, `onUnsubmitted`, `onDuplicada`)
 * y quien lo muestra decide adónde ir. La página de plantillas navega como siempre; la de la campaña
 * asocia la plantilla a la campaña y vuelve al asistente. En `modo="campana"` el enlace de volver pasa a
 * ser «Volver a la campaña» (pregunta antes de tirar cambios sin guardar), y los enlaces a Configuración
 * abren otra pestaña: seguirlos en la misma perdería lo que el operador escribió en la plantilla.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode, RefObject } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Copy, Eye, Image as ImageIcon, Info, ListRestart, Loader2,
  Save, Send, XCircle,
} from "lucide-react";
import type {
  BotonPlantilla, CampoDisponible, CampoVariable, ConfigCuenta, PlantillaBorrador,
  PlantillaDetalle, Proposito, ReglaResultado, TipoPlantilla,
} from "../../types";
import { oficialApi } from "../../api";
import { useValidacionPlantilla } from "../../hooks/useValidacionPlantilla";
import {
  aplicarReglasDeCategoria, categoriaPara, componerLocal, conEjemplos,
  huellaBorrador, limiteCuerpo, notaDeEjemplos, previaLocal, puedeEnviarAAprobacion, reglasFallidasPorCampo, tiposHabilitados,
} from "../../logic/plantilla";
import { detectarNumeros, insertarVariable, renumerar, siguienteNumero, sincronizarVariables, tieneSaltos } from "../../logic/variables";
import { esErrorDeClave, leerError, leerRespuesta, TEXTO_ERROR_CLAVE, textoError } from "../../logic/respuesta";
import { CATEGORIA, PROPOSITO, TIPO_PLANTILLA } from "../../logic/textos";
import { mesEnPalabras, usdPorMensaje } from "../../logic/formato";
import { ARCHIVO_IMAGEN_RE, conImagenPorCampana, imagenPorCampana, partirLinkDeImagen, sinImagenPorCampana } from "../../logic/imagenes";
import { conClic, numerosDeClic, sinClic } from "../../logic/clics";
import WizardStepper from "../../../campana-nueva/components/WizardStepper";
import type { Fase, Paso } from "../../../campana-nueva/components/WizardStepper";
import { PreviaWhatsApp } from "../PreviaWhatsApp";
import { SemaforoReglas, ErroresDeCampo } from "../SemaforoReglas";
import { EditorBotones } from "../EditorBotones";
import { ElegirImagen } from "../ElegirImagen";
import { InsertarVariable } from "../InsertarVariable";
import { SelectorEmojis } from "../SelectorEmojis";
import { contarEmojis } from "../../logic/emojis";
import { TablaVariables } from "../TablaVariables";
import { Tarjeta } from "../Tarjeta";

const R = "/activa/oficial";
const enc = encodeURIComponent;
const MAX_NOMBRE = 80;
const MAX_PIE = 60;
const MAX_ENCABEZADO = 60;

const PASOS: Paso[] = [
  { key: "proposito", label: "Propósito", desc: "Qué querés mandar" },
  { key: "contenido", label: "Contenido", desc: "Texto, imagen y botones" },
  { key: "chequeo", label: "Ejemplos y chequeo", desc: "Ejemplos para Meta y reglas" },
];
const FASES: Fase[] = [
  { key: "proposito", label: "Propósito", pasos: [0] },
  { key: "contenido", label: "Contenido", pasos: [1] },
  { key: "chequeo", label: "Ejemplos y chequeo", pasos: [2] },
];

const IDIOMA: Record<string, string> = { es: "Español", es_AR: "Español de Argentina" };

/** Texto del `window.confirm` al volver a la campaña con cambios sin guardar. */
export const CONFIRMAR_CERRAR = "Tenés cambios sin guardar en la plantilla. ¿Salir igual?";

export interface PropsEditorPlantilla {
  inicial: PlantillaBorrador; idInicial?: string; avisoInicial?: string;
  config: ConfigCuenta; campos: CampoDisponible[]; camposFallaron: boolean;
  modo: "pagina" | "campana";
  /** La plantilla se creó (primer guardado). */
  onCreada: (id: string) => void;
  /** Se mandó a aprobación de Meta. */
  onEnviada: (p: PlantillaDetalle) => void;
  /** Se creó una copia (el nombre ya estaba usado en WhatsApp). */
  onDuplicada: (id: string) => void;
  /** Twilio creó el Content pero no aceptó el pedido de aprobación: la plantilla quedó `unsubmitted`. */
  onUnsubmitted: (id: string, texto: string) => void;
  /** Sólo en la campaña: «Volver a la campaña». Con cambios sin guardar, el editor pide confirmación antes de llamarlo. */
  onCerrar?: () => void;
  /**
   * Sólo en la campaña: el título de la página. Va debajo del «Volver a la campaña», como en todas las
   * páginas; el volver lo pone el editor porque es el único que sabe si hay cambios sin guardar.
   */
  encabezado?: ReactNode;
}

/** Un enlace a Configuración: en la plantilla de una campaña abre otra pestaña para no perder lo escrito. */
function LinkConfig({ to, externo, className, children }: { to: string; externo: boolean; className?: string; children: ReactNode }) {
  return externo
    ? <Link to={to} target="_blank" rel="noopener" className={className}>{children}</Link>
    : <Link to={to} className={className}>{children}</Link>;
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Editor
// ─────────────────────────────────────────────────────────────────────────────────────────────

type ErrorEnvio = { texto: string; accion?: "config" | "duplicar" };

/** Deja el borrador coherente con el tipo nuevo: lo que el tipo no admite no queda escondido. */
function conTipo(b: PlantillaBorrador, t: TipoPlantilla): PlantillaBorrador {
  const botonesPermitidos: Record<TipoPlantilla, BotonPlantilla["tipo"][]> = {
    "twilio/text": [],
    "twilio/media": [],
    "twilio/quick-reply": ["QUICK_REPLY"],
    "twilio/call-to-action": ["URL", "PHONE_NUMBER"],
    "whatsapp/card": ["QUICK_REPLY", "URL"],
  };
  // Un campo que el tipo nuevo no muestra se saca: si quedara, el validador lo rechazaría
  // (`media_solo_media`, `encabezado`) y el operador no tendría dónde corregirlo.
  const out: PlantillaBorrador = { ...b, tipo: t, botones: (b.botones || []).filter((x) => botonesPermitidos[t].includes(x.tipo)) };
  if (t !== "twilio/media" && t !== "whatsapp/card") delete out.mediaUrl;
  if (t !== "whatsapp/card") delete out.encabezadoTexto;
  return out;
}

export function EditorPlantilla({
  inicial, idInicial, avisoInicial, config, campos, camposFallaron, modo,
  onCreada, onEnviada, onDuplicada, onUnsubmitted, onCerrar, encabezado,
}: PropsEditorPlantilla) {
  const enCampana = modo === "campana";
  const reglasConfig = useMemo(() => ({ textoBaja: config.textoBaja, botonBaja: config.botonBaja }), [config.textoBaja, config.botonBaja]);
  const aplicar = useCallback((b: PlantillaBorrador, anterior?: PlantillaBorrador): PlantillaBorrador => {
    const r = aplicarReglasDeCategoria(b, reglasConfig, anterior);
    return { ...r, variables: sincronizarVariables(r) };
  }, [reglasConfig]);

  // Una plantilla ya guardada se abre como está: sin `anterior`, la baja que el operador sacó volvería.
  const [borrador, setBorrador] = useState<PlantillaBorrador>(() => aplicar(inicial, idInicial ? inicial : undefined));
  const [id, setId] = useState<string | undefined>(idInicial);
  const [paso, setPaso] = useState(0);
  const [maxAlcanzado, setMaxAlcanzado] = useState(0);
  const [sucio, setSucio] = useState(false);
  const [aviso, setAviso] = useState(avisoInicial || "");
  const [guardando, setGuardando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [errorGuardar, setErrorGuardar] = useState("");
  const [errorEnvio, setErrorEnvio] = useState<ErrorEnvio | null>(null);
  const [duplicando, setDuplicando] = useState(false);
  const [verPrevia, setVerPrevia] = useState(false);
  // Reglas que devolvió `enviar-aprobacion` (422 PLANTILLA_INVALIDA): el backend las evaluó
  // contra las OTRAS plantillas de la marca y mandan sobre la validación en vivo mientras el
  // borrador sea el mismo.
  const [reglasServidor, setReglasServidor] = useState<{ huella: string; reglas: ReglaResultado[] } | null>(null);

  const borradorRef = useRef(borrador);
  borradorRef.current = borrador;
  const textoRef = useRef<HTMLTextAreaElement>(null);
  const semaforoRef = useRef<HTMLDivElement>(null);
  const cursorPendiente = useRef<number | null>(null);

  const { resultado, huellaResultado, validando, error: errorValidacion, reintentar: reintentarValidacion } = useValidacionPlantilla(borrador, id);
  const huellaActual = huellaBorrador(borrador);
  const validacionAlDia = !!resultado && huellaResultado === huellaActual;
  const servidorManda = !!reglasServidor && reglasServidor.huella === huellaActual;
  const reglas: ReglaResultado[] = servidorManda ? reglasServidor!.reglas : resultado?.reglas ?? [];
  const porCampo = reglasFallidasPorCampo(reglas);
  const fallanServidor = servidorManda && reglasServidor!.reglas.some((r) => r.nivel === "error" && !r.ok);
  const puedeEnviar = puedeEnviarAAprobacion({ resultado, huellaResultado, huellaActual, validando }) && !fallanServidor;

  const actualizar = (fn: (b: PlantillaBorrador) => PlantillaBorrador) => {
    setBorrador((prev) => aplicar(fn(prev), prev));
    setSucio(true);
    setGuardadoOk(false);
  };

  // Sin `useBlocker` (BrowserRouter, no data router): al menos se avisa al cerrar o recargar.
  useEffect(() => {
    if (!sucio) return;
    const h = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [sucio]);

  // En la campaña el editor se ve adentro de la sección: las pestañas, los avisos de arriba (el de marca
  // frenada lleva a Configuración) y el menú lateral navegan en ESTA pestaña, y un link de la SPA no
  // dispara `beforeunload`. Se pregunta acá, antes de que el `Link` navegue: en captura sobre `document`
  // corre antes que React, y sin la propagación el `Link` ni se entera. Lo que abre otra pestaña o sale
  // del sitio no pierde nada.
  useEffect(() => {
    if (!enCampana || !sucio) return;
    const h = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target instanceof Element ? e.target.closest<HTMLAnchorElement>("a[href]") : null;
      if (!a || a.target === "_blank" || a.origin !== window.location.origin) return;
      if (window.confirm(CONFIRMAR_CERRAR)) return;
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener("click", h, true);
    return () => document.removeEventListener("click", h, true);
  }, [enCampana, sucio]);

  // Después de insertar una variable, el cursor queda justo después de `{{n}}`.
  useEffect(() => {
    if (cursorPendiente.current === null || !textoRef.current) return;
    const c = cursorPendiente.current;
    cursorPendiente.current = null;
    textoRef.current.focus();
    textoRef.current.setSelectionRange(c, c);
  }, [borrador.cuerpo]);

  useEffect(() => {
    if (!guardadoOk) return;
    const t = setTimeout(() => setGuardadoOk(false), 2000);
    return () => clearTimeout(t);
  }, [guardadoOk]);

  const irA = (i: number) => {
    setPaso(i);
    setMaxAlcanzado((m) => Math.max(m, i));
    window.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  /**
   * Guarda lo que está en pantalla (crea o PATCH) y devuelve el id. Lanza lo que lance req():
   * quien llama decide cómo mostrarlo. `sucio` se apaga sólo si nadie tocó nada mientras tanto.
   */
  const guardarInterno = async (): Promise<string | null> => {
    const b = borradorRef.current;
    const huella = huellaBorrador(b);
    let pid = id;
    if (!pid) {
      const l = leerRespuesta<PlantillaDetalle>(await oficialApi.crearPlantilla(b));
      if (!l.ok || !l.data?.id) { setErrorGuardar(l.ok ? "El servidor no devolvió la plantilla creada." : textoError(l)); return null; }
      pid = l.data.id;
      setId(pid);
      onCreada(pid);
    } else {
      const l = leerRespuesta<PlantillaDetalle>(await oficialApi.editarPlantilla(pid, b));
      if (!l.ok) { setErrorGuardar(textoError(l)); return null; }
    }
    if (huellaBorrador(borradorRef.current) === huella) setSucio(false);
    return pid;
  };

  const textoErrorLanzado = (e: unknown): string => {
    const x = leerError(e);
    if (x.code === "PLANTILLA_NO_EDITABLE") return "Esta plantilla ya se mandó. Recargá la página.";
    if (esErrorDeClave(x.code)) return TEXTO_ERROR_CLAVE;
    return textoError(x);
  };

  const guardarBorrador = async () => {
    if (guardando || enviando) return;
    setGuardando(true);
    setErrorGuardar("");
    setErrorEnvio(null);
    try {
      const pid = await guardarInterno();
      if (pid) setGuardadoOk(true);
    } catch (e) {
      setErrorGuardar(textoErrorLanzado(e));
    } finally {
      setGuardando(false);
    }
  };

  const enviarAMeta = async () => {
    if (!puedeEnviar || enviando || guardando) return;
    setEnviando(true);
    setErrorEnvio(null);
    setErrorGuardar("");
    const huellaEnviada = huellaBorrador(borradorRef.current);
    let pid: string | null = null;
    try {
      pid = await guardarInterno();
      if (!pid) return;
      const l = leerRespuesta<PlantillaDetalle>(await oficialApi.enviarAAprobacion(pid));
      if (!l.ok) {
        if (l.code === "PLANTILLA_INVALIDA") {
          const rs = Array.isArray(l.extra.reglas) ? (l.extra.reglas as ReglaResultado[]) : [];
          setReglasServidor({ huella: huellaEnviada, reglas: rs });
          setErrorEnvio({ texto: l.error || "Meta no la aceptaría así: corregí lo que está en rojo." });
          setPaso(2);
          setMaxAlcanzado(2);
          // jsdom no tiene scrollIntoView: el `?.` evita que un test (o un navegador viejo) se caiga.
          setTimeout(() => semaforoRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" }), 0);
        } else if (l.code === "CUENTA_NO_VERIFICADA") {
          setErrorEnvio({ texto: "Primero conectá tu número de WhatsApp.", accion: "config" });
        } else if (l.code === "MARCA_FRENADA") {
          setErrorEnvio({ texto: "La marca está frenada: no se pueden mandar plantillas.", accion: "config" });
        } else {
          setErrorEnvio({ texto: textoError(l) });
        }
        return;
      }
      // Sin el detalle en la respuesta igual se avisa con el id: quien escucha vuelve a traer la plantilla.
      onEnviada(l.data?.id ? l.data : ({ ...(l.data || {}), id: pid } as PlantillaDetalle));
    } catch (e) {
      const x = leerError(e);
      if (x.code === "NOMBRE_EN_USO") {
        setErrorEnvio({ texto: "WhatsApp ya tiene una plantilla con ese nombre. Duplicala: la copia sale con otro nombre.", accion: "duplicar" });
      } else if (x.code === "TWILIO_ERROR" || x.status === 502) {
        const texto = `No pudimos mandar la plantilla a revisión: ${x.error || "sin detalle"}. Meta todavía no la recibió.`;
        setErrorEnvio({ texto });
        // Si el Content se creó pero la aprobación falló, la plantilla quedó «Borrador en Twilio»:
        // su detalle ofrece «Reintentar envío a aprobación» sin crear otro Content.
        if (pid) {
          try {
            const d = leerRespuesta<PlantillaDetalle>(await oficialApi.plantilla(pid));
            if (d.ok && d.data?.estado === "unsubmitted") onUnsubmitted(pid, texto);
          } catch {
            // Sin poder confirmar el estado, se queda en el editor con el error a la vista.
          }
        }
      } else {
        setErrorEnvio({ texto: textoErrorLanzado(e) });
      }
    } finally {
      setEnviando(false);
    }
  };

  /** «Volver a la campaña»: con cambios sin guardar, se pregunta antes de perderlos. */
  const cerrar = () => {
    if (sucio && !window.confirm(CONFIRMAR_CERRAR)) return;
    onCerrar?.();
  };

  const duplicar = async () => {
    if (!id || duplicando) return;
    setDuplicando(true);
    try {
      const l = leerRespuesta<PlantillaDetalle>(await oficialApi.duplicarPlantilla(id));
      if (!l.ok || !l.data?.id) { setErrorEnvio({ texto: l.ok ? "El servidor no devolvió la copia." : textoError(l) }); return; }
      onDuplicada(l.data.id);
    } catch (e) {
      setErrorEnvio({ texto: textoErrorLanzado(e) });
    } finally {
      setDuplicando(false);
    }
  };

  // ── Previa ───────────────────────────────────────────────────────────────────────────────
  const esTarjeta = borrador.tipo === "whatsapp/card";
  const textoPrevia = validacionAlDia ? resultado!.previa : previaLocal(borrador);
  const notaEjemplos = notaDeEjemplos(borrador);
  const mediaPrevia = borrador.mediaUrl && (borrador.tipo === "twilio/media" || esTarjeta) ? conEjemplos(borrador.mediaUrl, borrador) : undefined;

  const motivoDeshabilitado = validando
    ? "Revisando…"
    : fallanServidor || (validacionAlDia && !puedeEnviar)
      ? "Corregí lo que está en rojo"
      : errorValidacion
        ? `No pudimos validar: ${errorValidacion}`
        : "";

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {enCampana ? (
          // Un botón con cara de enlace: el editor no navega, avisa (`onCerrar`) después de preguntar.
          // Apagado mientras se guarda, se manda o se duplica: el resultado («Mandada a Meta…», la copia)
          // llegaría con la página ya ida y se perdería. Con el envío en vuelo, además, `sucio` ya se apagó
          // y saldría sin preguntar.
          <button type="button" onClick={cerrar} disabled={guardando || enviando || duplicando}
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 disabled:opacity-50 disabled:cursor-not-allowed">
            <ArrowLeft size={14} /> Volver a la campaña
          </button>
        ) : (
          <Link to={id ? `${R}/plantillas/${enc(id)}` : `${R}/plantillas`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
            <ArrowLeft size={14} /> {id ? "Ver la plantilla" : "Plantillas"}
          </Link>
        )}
        <p className="text-xs text-slate-400">{id ? "Editando un borrador" : "Plantilla nueva"}{sucio ? " · con cambios sin guardar" : ""}</p>
      </div>

      {encabezado}

      {aviso && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span className="flex-1">{aviso}</span>
          <button type="button" onClick={() => setAviso("")} className="text-xs font-semibold hover:underline">Cerrar</button>
        </div>
      )}

      <WizardStepper steps={PASOS} fases={FASES} current={paso} maxReached={maxAlcanzado} onGo={irA} />

      <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-5 items-start">
        <div className="space-y-5 min-w-0">
          {paso === 0 && (
            <PasoProposito
              borrador={borrador}
              actualizar={actualizar}
              porCampo={porCampo}
              costo={validacionAlDia && resultado!.costoPorMensaje?.categoria === borrador.categoria ? (resultado!.costoPorMensaje.usd ?? null) : null}
              costoCliente={validacionAlDia && resultado!.costoPorMensaje?.categoria === borrador.categoria ? resultado!.costoPorMensaje.clienteUSD : null}
              linkExterno={enCampana}
            />
          )}
          {paso === 1 && (
            <PasoContenido
              borrador={borrador}
              actualizar={actualizar}
              porCampo={porCampo}
              config={config}
              campos={campos}
              camposFallaron={camposFallaron}
              textoRef={textoRef}
              onCursor={(c) => { cursorPendiente.current = c; }}
              linkExterno={enCampana}
            />
          )}
          {paso === 2 && (
            <div className="space-y-5">
              <Tarjeta titulo="Ejemplos para Meta" sub={'Meta pide un ejemplo por variable para aprobarla. Usá un valor realista: "Sofía", no "nombre". El ejemplo no le llega a nadie: cada persona recibe su propio dato.'}>
                <TablaVariables
                  variables={borrador.variables}
                  campos={campos}
                  reglas={reglas}
                  numeroImagen={imagenPorCampana(borrador.mediaUrl)?.numero ?? null}
                  numerosClic={numerosDeClic(borrador.botones)}
                  onChange={(variables) => actualizar((b) => ({ ...b, variables }))}
                />
              </Tarjeta>

              <div ref={semaforoRef}>
                <SemaforoReglas reglas={reglas} validando={validando && !servidorManda} />
              </div>

              <div className="flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                <Info size={16} className="flex-shrink-0 mt-0.5 text-slate-400" />
                <p>
                  <span className="font-semibold">Antes de mandar:</span> Meta tarda de minutos a 48 h. Una vez enviada <strong>no se puede editar</strong>:
                  para cambiarla se duplica y la copia vuelve a revisión.
                </p>
              </div>

              {errorGuardar && <p className="text-sm text-red-600" role="alert">{errorGuardar}</p>}
              {errorEnvio && (
                <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 space-y-2">
                  <p className="flex items-start gap-2"><XCircle size={16} className="flex-shrink-0 mt-0.5" />{errorEnvio.texto}</p>
                  {errorEnvio.accion === "config" && (
                    <LinkConfig to={`${R}/configuracion?seccion=cuenta`} externo={enCampana} className="inline-block font-semibold text-red-800 underline">Ir a Configuración</LinkConfig>
                  )}
                  {errorEnvio.accion === "duplicar" && id && (
                    <button type="button" onClick={duplicar} disabled={duplicando}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-red-200 px-3 py-1.5 text-sm font-semibold text-red-800 hover:bg-red-100 disabled:opacity-50">
                      {duplicando ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} Duplicar y editar
                    </button>
                  )}
                </div>
              )}

              <div className="flex items-center gap-3 flex-wrap">
                <button type="button" onClick={guardarBorrador} disabled={guardando || enviando}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                  {guardando ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Guardar borrador
                </button>
                <button type="button" onClick={enviarAMeta} disabled={!puedeEnviar || enviando || guardando}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  {enviando ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Enviar a aprobación de Meta
                </button>
                {guardadoOk && <span className="inline-flex items-center gap-1 text-sm text-emerald-700"><CheckCircle2 size={15} /> Guardado</span>}
                {!puedeEnviar && !enviando && motivoDeshabilitado && <span className="text-xs text-slate-500">{motivoDeshabilitado}</span>}
                {!validando && !!errorValidacion && !fallanServidor && !(validacionAlDia && !puedeEnviar) && (
                  <button type="button" onClick={reintentarValidacion} className="text-xs font-semibold text-violet-700 hover:underline">
                    Reintentar
                  </button>
                )}
              </div>
            </div>
          )}

          {paso < 2 && (
            <div className="flex items-center justify-between gap-3">
              <button type="button" onClick={() => irA(paso - 1)} disabled={paso === 0}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-0">
                <ArrowLeft size={15} /> Anterior
              </button>
              <button type="button" onClick={() => irA(paso + 1)} disabled={paso === 0 && !borrador.nombreVisible.trim()}
                className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed">
                Siguiente <ArrowRight size={15} />
              </button>
            </div>
          )}
          {paso === 2 && (
            <button type="button" onClick={() => irA(1)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800">
              <ArrowLeft size={14} /> Volver al contenido
            </button>
          )}
        </div>

        <aside className="lg:sticky lg:top-4 space-y-2">
          <button type="button" onClick={() => setVerPrevia((v) => !v)} aria-expanded={verPrevia}
            className="lg:hidden w-full inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700">
            <Eye size={15} /> {verPrevia ? "Ocultar la previa" : "Ver cómo queda"}
          </button>
          <div aria-live="polite" className={`${verPrevia ? "block" : "hidden"} lg:block`}>
            <PreviaWhatsApp
              texto={textoPrevia}
              mediaUrl={mediaPrevia}
              botones={borrador.botones}
              pie={esTarjeta ? borrador.pie : undefined}
              encabezadoTexto={esTarjeta ? borrador.encabezadoTexto : undefined}
              actualizando={!validacionAlDia}
            />
            {notaEjemplos && <p className="mt-2 text-xs text-slate-500" data-testid="nota-ejemplos">{notaEjemplos}</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Paso 1 — Propósito
// ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * `costo`: lo que cobran Meta y Twilio por mensaje (costo real); no le llega a una persona de un cliente.
 * `costoCliente`: lo mismo con el recargo de Deenex.
 */
function PasoProposito({ borrador, actualizar, porCampo, costo, costoCliente, linkExterno }: {
  borrador: PlantillaBorrador; actualizar: (fn: (b: PlantillaBorrador) => PlantillaBorrador) => void;
  porCampo: Record<string, ReglaResultado[]>; costo: number | null; costoCliente: number | null; linkExterno: boolean;
}) {
  const [nombreTocado, setNombreTocado] = useState(false);
  const elegir = (p: Proposito) => actualizar((b) => ({
    ...b,
    proposito: p,
    // En «aviso» se conserva la elección del toggle; si venía de una promo, arranca como Utilidad.
    categoria: categoriaPara(p, p === "aviso" ? (b.proposito === "aviso" ? b.categoria : "UTILITY") : undefined),
  }));
  const categoriaTexto = CATEGORIA[borrador.categoria];

  return (
    <div className="space-y-5">
      <Tarjeta titulo="¿Qué querés mandar?">
        <div role="radiogroup" aria-label="Propósito de la plantilla" className="grid sm:grid-cols-3 gap-3">
          {(Object.keys(PROPOSITO) as Proposito[]).map((p) => {
            const activo = borrador.proposito === p;
            return (
              <button key={p} type="button" role="radio" aria-checked={activo} onClick={() => elegir(p)}
                className={`text-left rounded-xl border-2 px-4 py-3 transition-colors ${activo ? "border-violet-600 bg-violet-50" : "border-slate-200 hover:border-slate-300"}`}>
                <p className="text-sm font-semibold text-slate-900">{PROPOSITO[p].titulo}</p>
                <p className="text-xs text-slate-500 mt-1">{PROPOSITO[p].detalle}</p>
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-2">{CATEGORIA[PROPOSITO[p].categoriaSugerida]}</p>
              </button>
            );
          })}
        </div>

        {borrador.proposito === "aviso" && (
          <label className="mt-4 flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={borrador.categoria === "MARKETING"}
              onChange={(e) => actualizar((b) => ({ ...b, categoria: categoriaPara("aviso", e.target.checked ? "MARKETING" : "UTILITY") }))}
              className="rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
            Tiene algo de promoción
          </label>
        )}

        <div className={`mt-4 rounded-xl px-4 py-3 text-sm ${borrador.categoria === "MARKETING" ? "bg-violet-50 text-violet-900" : "bg-slate-50 text-slate-800"}`}>
          <p>
            {costo === null && costoCliente !== null
              ? <>Meta la cobra como <strong>{categoriaTexto}</strong>: {usdPorMensaje(costoCliente)} por mensaje entregado.</>
              : <>Meta y Twilio la cobran como <strong>{categoriaTexto}</strong>: {costo === null ? "…" : usdPorMensaje(costo)} por mensaje entregado.</>}
          </p>
          {costo !== null && costoCliente !== null && usdPorMensaje(costoCliente) !== usdPorMensaje(costo) && (
            <p className="text-xs mt-1">{`A la marca se le cobra ${usdPorMensaje(costoCliente)}, con el recargo de Deenex.`}</p>
          )}
          <p className="text-xs mt-1 opacity-80">
            Si es una promoción, tiene que ser Marketing: si la mandás como Utilidad, Meta la rechaza o la recategoriza y el costo sube igual.
          </p>
        </div>
        <ErroresDeCampo reglas={[...(porCampo.categoria || []), ...(porCampo.proposito || [])]} />
      </Tarjeta>

      <Tarjeta titulo="Nombre para reconocerla">
        <div className="relative">
          <input
            aria-label="Nombre para reconocerla"
            value={borrador.nombreVisible}
            onChange={(e) => actualizar((b) => ({ ...b, nombreVisible: e.target.value.slice(0, MAX_NOMBRE) }))}
            onBlur={() => setNombreTocado(true)}
            placeholder={`Ej.: Te extrañamos ${mesEnPalabras()}`}
            className="w-full rounded-xl border border-slate-200 px-3 py-2 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 tabular-nums">{borrador.nombreVisible.length}/{MAX_NOMBRE}</span>
        </div>
        {/* Recién abierta, la plantilla no tiene nombre: el error en rojo aparecía antes de que nadie
            escribiera nada. Hasta que se toca el campo (o si ya tiene algo escrito) va una indicación. */}
        {nombreTocado || borrador.nombreVisible.length > 0
          ? <ErroresDeCampo reglas={porCampo.nombreVisible} />
          : <p className="text-xs text-slate-500 mt-1.5">Lo ves sólo vos, para encontrarla en la lista. Escribilo para seguir.</p>}
        <p className="text-xs text-slate-400 mt-3">
          Idioma: <span className="text-slate-600">{IDIOMA[borrador.idioma] || borrador.idioma}</span> · es el de la marca y se cambia en{" "}
          <LinkConfig to={`${R}/configuracion?seccion=envio`} externo={linkExterno} className="text-violet-700 hover:underline">Configuración → Envío</LinkConfig>.
        </p>
        <ErroresDeCampo reglas={porCampo.idioma} />
      </Tarjeta>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Paso 2 — Contenido
// ─────────────────────────────────────────────────────────────────────────────────────────────

type Encabezado = "ninguno" | "imagen" | "titulo";

function PasoContenido({ borrador, actualizar, porCampo, config, campos, camposFallaron, textoRef, onCursor, linkExterno }: {
  borrador: PlantillaBorrador; actualizar: (fn: (b: PlantillaBorrador) => PlantillaBorrador) => void;
  porCampo: Record<string, ReglaResultado[]>; config: ConfigCuenta; campos: CampoDisponible[]; camposFallaron: boolean;
  textoRef: RefObject<HTMLTextAreaElement>; onCursor: (c: number) => void; linkExterno: boolean;
}) {
  const habilitados = tiposHabilitados(borrador.categoria);
  const marketing = borrador.categoria === "MARKETING";
  const esTarjeta = borrador.tipo === "whatsapp/card";
  const conBotones = borrador.tipo === "twilio/quick-reply" || borrador.tipo === "twilio/call-to-action" || esTarjeta;
  // el tipo que lleva botones y conserva lo que ya hay: la imagen pasa a la tarjeta tal cual
  const destinoConBotones: TipoPlantilla = borrador.tipo === "twilio/media" ? "whatsapp/card" : "twilio/quick-reply";
  const largo = componerLocal(borrador).length;
  const limite = limiteCuerpo(borrador.tipo);
  const saltos = tieneSaltos(detectarNumeros(borrador));
  // Un título escrito manda sobre una imagen vacía: el backend puede devolver `mediaUrl: ""` en una
  // tarjeta con título, y leerla como «imagen» escondería el título que la plantilla sí tiene.
  const encabezado: Encabezado = (borrador.encabezadoTexto || "").trim()
    ? "titulo"
    : borrador.mediaUrl !== undefined ? "imagen" : borrador.encabezadoTexto !== undefined ? "titulo" : "ninguno";

  const insertar = (v: { alias: string; campoSugerido?: CampoVariable; ejemplo: string }) => {
    const el = textoRef.current;
    const inicio = el ? el.selectionStart : borrador.cuerpo.length;
    const fin = el ? el.selectionEnd : borrador.cuerpo.length;
    actualizar((b) => {
      const n = siguienteNumero(b);
      const r = insertarVariable(b.cuerpo, inicio, fin, n);
      onCursor(r.cursor);
      const conTexto: PlantillaBorrador = { ...b, cuerpo: r.texto };
      const vars = sincronizarVariables(conTexto).map((x) =>
        x.numero === n ? { ...x, alias: v.alias || x.alias, ejemplo: v.ejemplo || x.ejemplo, ...(v.campoSugerido ? { campoSugerido: v.campoSugerido } : {}) } : x);
      return { ...conTexto, variables: vars };
    });
  };

  // un emoji va donde está el cursor (o reemplaza lo seleccionado), como una variable
  const insertarEmoji = (emoji: string) => {
    const el = textoRef.current;
    const inicio = el ? el.selectionStart : borrador.cuerpo.length;
    const fin = el ? el.selectionEnd : borrador.cuerpo.length;
    actualizar((b) => {
      onCursor(inicio + emoji.length);
      return { ...b, cuerpo: b.cuerpo.slice(0, inicio) + emoji + b.cuerpo.slice(fin) };
    });
  };

  const elegirEncabezado = (e: Encabezado) => actualizar((b) => {
    const out: PlantillaBorrador = { ...b };
    delete out.mediaUrl;
    delete out.encabezadoTexto;
    // Imagen o título, nunca los dos (D-A): el que no se eligió se borra.
    if (e === "imagen") out.mediaUrl = b.mediaUrl ?? "";
    if (e === "titulo") out.encabezadoTexto = b.encabezadoTexto ?? "";
    return out;
  });

  return (
    <div className="space-y-5">
      <Tarjeta titulo="Tipo de mensaje">
        <div role="radiogroup" aria-label="Tipo de plantilla" className="grid sm:grid-cols-2 gap-3">
          {(Object.keys(TIPO_PLANTILLA) as TipoPlantilla[]).map((t) => {
            const activo = borrador.tipo === t;
            const habilitado = habilitados.includes(t);
            return (
              <button key={t} type="button" role="radio" aria-checked={activo} disabled={!habilitado}
                onClick={() => actualizar((b) => conTipo(b, t))}
                className={`text-left rounded-xl border-2 px-4 py-3 transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  activo ? "border-violet-600 bg-violet-50" : habilitado ? "border-slate-200 hover:border-slate-300" : "border-slate-100 bg-slate-50"
                }`}>
                <p className="text-sm font-semibold text-slate-900">{TIPO_PLANTILLA[t].titulo}</p>
                <p className="text-xs text-slate-500 mt-1">{TIPO_PLANTILLA[t].detalle}</p>
              </button>
            );
          })}
        </div>
        {marketing && (
          <p className="text-xs text-slate-500 mt-3">
            El botón de baja sólo entra con botones de respuesta rápida o con la tarjeta.
          </p>
        )}
        <ErroresDeCampo reglas={porCampo.tipo} />
      </Tarjeta>

      {esTarjeta && (
        <Tarjeta titulo="Encabezado" sub="Opcional: una imagen o un título arriba del texto. Nunca los dos.">
          <div role="radiogroup" aria-label="Encabezado de la tarjeta" className="flex flex-wrap gap-2">
            {([["ninguno", "Sin encabezado"], ["imagen", "Imagen"], ["titulo", "Título"]] as [Encabezado, string][]).map(([k, t]) => (
              <button key={k} type="button" role="radio" aria-checked={encabezado === k} onClick={() => elegirEncabezado(k)}
                className={`rounded-lg border px-3 py-1.5 text-sm font-medium ${encabezado === k ? "border-violet-600 bg-violet-50 text-violet-800" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                {t}
              </button>
            ))}
          </div>
          {encabezado === "titulo" && (
            <div className="mt-3">
              <div className="relative">
                <input aria-label="Título del encabezado" value={borrador.encabezadoTexto || ""}
                  onChange={(e) => actualizar((b) => ({ ...b, encabezadoTexto: e.target.value }))}
                  placeholder="Ej.: ¡Volvió el 2x1!"
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
                <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-[11px] tabular-nums ${(borrador.encabezadoTexto || "").length > MAX_ENCABEZADO ? "text-red-600 font-semibold" : "text-slate-400"}`}>
                  {(borrador.encabezadoTexto || "").length}/{MAX_ENCABEZADO}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Sin variables: el título es igual para todos.</p>
            </div>
          )}
          {encabezado === "imagen" && <CampoImagen borrador={borrador} actualizar={actualizar} />}
          <ErroresDeCampo reglas={[...(porCampo.encabezadoTexto || []), ...(porCampo.mediaUrl || [])]} />
        </Tarjeta>
      )}

      {borrador.tipo === "twilio/media" && (
        <Tarjeta titulo="Imagen">
          <CampoImagen borrador={borrador} actualizar={actualizar} />
          <ErroresDeCampo reglas={porCampo.mediaUrl} />
        </Tarjeta>
      )}

      <Tarjeta titulo="Texto">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <InsertarVariable
            campos={camposFallaron ? [] : campos}
            aviso={camposFallaron ? "No pudimos traer los datos de los clientes" : undefined}
            onElegir={insertar}
          />
          <SelectorEmojis emojis={contarEmojis(componerLocal(borrador))} onElegir={insertarEmoji} />
          {saltos && (
            <button type="button" onClick={() => actualizar((b) => renumerar(b))}
              className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100">
              <ListRestart size={13} /> Renumerar variables
            </button>
          )}
          <span className={`ml-auto text-[11px] tabular-nums ${largo > limite ? "text-red-600 font-semibold" : "text-slate-400"}`}>{largo}/{limite}</span>
        </div>
        <textarea
          ref={textoRef}
          aria-label="Texto del mensaje"
          value={borrador.cuerpo}
          onChange={(e) => actualizar((b) => ({ ...b, cuerpo: e.target.value }))}
          rows={8}
          placeholder="Hola {{1}}, te extrañamos. Volvé esta semana y tenés {{2}} en tu próximo pedido."
          className={`w-full rounded-xl border px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-violet-500 ${largo > limite ? "border-red-300" : "border-slate-200"}`}
        />
        <p className="text-[11px] text-slate-400 mt-1">Negrita: *texto* · Cursiva: _texto_. No empieces ni termines con una variable.</p>
        <ErroresDeCampo reglas={porCampo.cuerpo} />
      </Tarjeta>

      <Tarjeta titulo={esTarjeta ? "Pie de la tarjeta" : "Pie"}>
        <div>
          <div className="relative">
            <input aria-label="Pie del mensaje" value={borrador.pie || ""}
              onChange={(e) => actualizar((b) => {
                const pie = e.target.value;
                const out = { ...b };
                if (pie) out.pie = pie; else delete out.pie;
                return out;
              })}
              placeholder={marketing ? "Sin texto de baja" : "Opcional"}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 pr-16 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-[11px] tabular-nums ${(borrador.pie || "").length > MAX_PIE ? "text-red-600 font-semibold" : "text-slate-400"}`}>
              {(borrador.pie || "").length}/{MAX_PIE}
            </span>
          </div>
          {marketing ? (
            <p className="text-[11px] text-slate-500 mt-1">
              Cómo darse de baja en esta plantilla. Viene el de{" "}
              <LinkConfig to={`${R}/configuracion?seccion=bajas`} externo={linkExterno} className="text-violet-700 hover:underline">Configuración</LinkConfig>
              {" "}y lo podés cambiar o borrar.
              {(borrador.pie || "") !== config.textoBaja && (
                <>
                  {" "}
                  <button type="button" onClick={() => actualizar((b) => ({ ...b, pie: config.textoBaja }))}
                    className="font-medium text-violet-700 hover:underline">Usar el de Configuración</button>
                </>
              )}
            </p>
          ) : (
            <p className="text-[11px] text-slate-400 mt-1">
              {esTarjeta ? "Va aparte, en gris, debajo del texto. Sin variables." : "Va como último párrafo del mensaje."}
            </p>
          )}
        </div>
        <ErroresDeCampo reglas={porCampo.pie} />
      </Tarjeta>

      {!conBotones && (
        // Antes la sección no aparecía y no había cómo saber dónde estaban los botones (Alan, 26/10/2026: «que se
        // puedan tocar varios botones»). Imagen con texto y texto solo no los llevan; la tarjeta y la respuesta rápida, sí.
        <Tarjeta titulo="Botones">
          <p className="text-sm text-slate-600">
            {borrador.tipo === "twilio/media"
              ? "La imagen con texto no lleva botones. La tarjeta lleva la misma imagen arriba y hasta 10 botones: de respuesta y hasta 2 links."
              : "El texto solo no lleva botones. Con botones de respuesta rápida van hasta 10, y la tarjeta suma hasta 2 links."}
          </p>
          {habilitados.includes(destinoConBotones) && (
            <button type="button" onClick={() => actualizar((b) => conTipo(b, destinoConBotones))}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-sm font-semibold text-violet-800 hover:bg-violet-100">
              {borrador.tipo === "twilio/media" ? "Pasar a tarjeta, con la imagen y botones" : "Pasar a botones de respuesta rápida"}
            </button>
          )}
        </Tarjeta>
      )}

      {conBotones && (
        <Tarjeta titulo="Botones">
          <EditorBotones
            tipo={borrador.tipo}
            categoria={borrador.categoria}
            botones={borrador.botones}
            botonBaja={config.botonBaja}
            onChange={(botones) => actualizar((b) => ({ ...b, botones }))}
            prefijoClics={config.prefijoClics}
            onClic={(i, activar) => actualizar((b) => (activar && config.prefijoClics ? conClic(b, i, config.prefijoClics) : sinClic(b, i)))}
          />
          <ErroresDeCampo reglas={porCampo.botones} />
        </Tarjeta>
      )}
    </div>
  );
}

/**
 * La imagen del encabezado. Fija (la misma en todas las campañas) o «se elige en cada campaña» (pedido de Alan del
 * 26/10/2026): ahí la URL termina en una variable y la imagen elegida acá es sólo la de ejemplo, la que ve Meta para
 * aprobar la plantilla. Sólo una imagen de la biblioteca se puede cambiar por campaña.
 */
function CampoImagen({ borrador, actualizar }: { borrador: PlantillaBorrador; actualizar: (fn: (b: PlantillaBorrador) => PlantillaBorrador) => void }) {
  const url = borrador.mediaUrl || "";
  const porCampana = imagenPorCampana(url);
  const ejemplo = porCampana ? (borrador.variables || []).find((v) => v.numero === porCampana.numero)?.ejemplo || "" : "";
  // la que se ve y se elige: con imagen por campaña, la de ejemplo
  const visible = porCampana ? (ARCHIVO_IMAGEN_RE.test(ejemplo) ? porCampana.prefijo + ejemplo : "") : url;
  const deLaBiblioteca = !!partirLinkDeImagen(visible);
  const elegir = (u: string) => actualizar((b) => (porCampana ? conImagenPorCampana(b, u) ?? { ...sinImagenPorCampana(b), mediaUrl: u } : { ...b, mediaUrl: u }));
  return (
    <div className="mt-3 space-y-2">
      {/* fase 2: subir o elegir una imagen ya subida pone el link solo */}
      <ElegirImagen url={visible} onElegir={elegir} />
      {(deLaBiblioteca || porCampana) && (
        <label className="flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" className="mt-0.5" checked={!!porCampana}
            onChange={(e) => actualizar((b) => (e.target.checked ? conImagenPorCampana(b, visible) ?? b : sinImagenPorCampana(b)))} />
          <span>
            Elegir la imagen en cada campaña
            <span className="block text-[11px] text-slate-500">
              {porCampana ? "Esta es la imagen de ejemplo que ve Meta para aprobar la plantilla. La que sale se elige al armar cada campaña." : "Así una misma plantilla sirve con otra foto en cada promo."}
            </span>
          </span>
        </label>
      )}
      {!porCampana && (
        <>
          <label className="text-xs font-semibold text-slate-600 flex items-center gap-1.5"><ImageIcon size={13} /> O pegá el link público de la imagen (https)</label>
          <input aria-label="URL pública de la imagen" value={url} inputMode="url" spellCheck={false}
            onChange={(e) => actualizar((b) => ({ ...b, mediaUrl: e.target.value.trim() }))}
            placeholder="https://tu.web/imagenes/promo.jpg"
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-500" />
          <p className="text-[11px] text-slate-400">Si la foto está en tu web, pegá su link. Tiene que abrirse sin iniciar sesión.</p>
        </>
      )}
      {/^https:\/\/\S+$/.test(visible) && !/\{\{/.test(visible) && (
        <img src={visible} alt="Miniatura de la imagen" className="h-20 w-32 object-cover rounded-lg border border-slate-200"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
      )}
    </div>
  );
}
